import type { AxiosInstance } from "axios";
import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import { AdminNetworkSchema } from "@shared/schemas/admin-network.schemas";
import {
  AdminNetworkSearchResultSchema,
  type AdminNetworkSearchResult,
} from "@shared/schemas/admin-network-search.schemas";
import { resolvePhotoUrl } from "@shared/utils/participant-photo-url.utils";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createNetworkAdminApi } from "./network-admin.api";

const NETWORK_STALE_TIME_MS = 60_000;
const SEARCH_STALE_TIME_MS = 5 * 60_000;

const QUERY_KEYS = {
  network: () => ["network-admin", "network"] as const,
  search: (query: string, eventIdPublic: EventIdPublic | null, deep: boolean) =>
    ["network-admin", "search", query, eventIdPublic, deep] as const,
};

export class NetworkAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createNetworkAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetNetwork: () =>
        this.query({
          key: QUERY_KEYS.network(),
          fn: () => api.getNetwork(),
          // Each node's photo resolves against the event its profile row lives in.
          parse: (dto) => {
            const network = AdminNetworkSchema.parse(dto);
            return {
              ...network,
              nodes: network.nodes.map((node) => ({
                ...node,
                photoUrl: resolvePhotoUrl(
                  node.photoUrl,
                  EventIdPublic.unwrap(node.profileEventIdPublic),
                ),
              })),
            };
          },
          staleTime: NETWORK_STALE_TIME_MS,
        }),

      useSearch: (
        query: string,
        eventIdPublic: EventIdPublic | null,
        deep: boolean,
      ): UseQueryResult<AdminNetworkSearchResult> =>
        this.query({
          key: QUERY_KEYS.search(query, eventIdPublic, deep),
          fn: () => api.search(query, eventIdPublic, deep),
          parse: (dto) => AdminNetworkSearchResultSchema.parse(dto),
          enabled: query !== "",
          staleTime: SEARCH_STALE_TIME_MS,
        }),
    };
  }
}
